import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";
import { Activity, BarChart2, BookOpen, Send } from "lucide-react";
import IndicadorTooltip from "@/components/IndicadorTooltip";

// ─── Pipeline Bar Chart ─────────────────────────────────────
const PIPELINE_LABELS: Record<string, string> = {
  PENDENTE: "Pendente",
  COLETA_EM_ANDAMENTO: "Em Coleta",
  AVALIACAO_CONCLUIDA: "Concluída",
  AVALIACAO_BLOQUEADA_SEM_RESPOSTAS: "Bloq. Sem Resp.",
  AVALIACAO_BLOQUEADA_DADOS_INSUFICIENTES: "Bloq. Insuf.",
};

const PIPELINE_COLORS: Record<string, string> = {
  PENDENTE: "#94a3b8",
  COLETA_EM_ANDAMENTO: "#3b82f6",
  AVALIACAO_CONCLUIDA: "#10b981",
  AVALIACAO_BLOQUEADA_SEM_RESPOSTAS: "#ef4444",
  AVALIACAO_BLOQUEADA_DADOS_INSUFICIENTES: "#f97316",
};

interface PipelineChartProps {
  pipeline: Record<string, number>;
  title?: string;
  loading?: boolean;
  tooltipKey?: string;
}

export function PipelineChart({ pipeline, title = "Pipeline de Avaliações", loading, tooltipKey }: PipelineChartProps) {
  if (loading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-5 w-48 mb-4" />
        <Skeleton className="h-[280px] w-full" />
      </Card>
    );
  }

  const data = Object.entries(PIPELINE_LABELS).map(([key, label]) => ({
    name: label,
    value: pipeline[key] || 0,
    fill: PIPELINE_COLORS[key] || "#888",
  }));

  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {tooltipKey && <IndicadorTooltip indicadorKey={tooltipKey} side="top" />}
        </div>
        <span className="text-xs text-muted-foreground">Total: {total.toLocaleString("pt-BR")}</span>
      </div>
      {total === 0 ? (
        <div className="flex flex-col items-center justify-center h-[280px] text-muted-foreground gap-3">
          <Activity size={40} className="opacity-30" />
          <div className="text-center">
            <p className="text-sm font-medium">Nenhuma avaliação registrada</p>
            <p className="text-xs mt-1 max-w-[260px]">
              O pipeline aparecerá aqui após a importação de colaboradores e criação de campanhas ativas.
            </p>
          </div>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" fontSize={12} />
            <YAxis type="category" dataKey="name" width={110} fontSize={11} tick={{ fill: "#64748b" }} />
            <Tooltip
              formatter={(value: number) => [value.toLocaleString("pt-BR"), "Quantidade"]}
              contentStyle={{ fontSize: 12 }}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={24}>
              {data.map((entry, index) => (
                <Cell key={index} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

// ─── Qualifier Pie Chart ────────────────────────────────────
interface QualifierChartProps {
  data: Array<{ label: string; count: number; color: string }>;
  title?: string;
  loading?: boolean;
  tooltipKey?: string;
}

export function QualifierChart({ data, title = "Distribuição de Qualificadores", loading, tooltipKey }: QualifierChartProps) {
  if (loading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-5 w-48 mb-4" />
        <Skeleton className="h-[280px] w-full" />
      </Card>
    );
  }

  const total = data.reduce((sum, d) => sum + d.count, 0);

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {tooltipKey && <IndicadorTooltip indicadorKey={tooltipKey} side="top" />}
        </div>
        <span className="text-xs text-muted-foreground">Total: {total.toLocaleString("pt-BR")}</span>
      </div>
      {total === 0 ? (
        <div className="flex flex-col items-center justify-center h-[280px] text-muted-foreground gap-3">
          <BarChart2 size={40} className="opacity-30" />
          <div className="text-center">
            <p className="text-sm font-medium">Nenhum resultado disponível</p>
            <p className="text-xs mt-1 max-w-[260px]">
              Os qualificadores de risco aparecerão aqui após a conclusão das avaliações psicossociais.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-6">
          <ResponsiveContainer width="60%" height={280}>
            <PieChart>
              <Pie
                data={data}
                dataKey="count"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                stroke="none"
              >
                {data.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string) => [value.toLocaleString("pt-BR"), name]}
                contentStyle={{ fontSize: 12 }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-col gap-2 w-[40%]">
            {data.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                <span className="text-muted-foreground truncate">{item.label}</span>
                <span className="ml-auto font-semibold tabular-nums">{item.count}</span>
                <span className="text-xs text-muted-foreground">
                  ({total > 0 ? Math.round((item.count / total) * 100) : 0}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── Campaign Status Summary ────────────────────────────────
const CAMPAIGN_COLORS: Record<string, string> = {
  DRAFT: "#94a3b8",
  ACTIVE: "#3b82f6",
  CLOSED: "#64748b",
};

interface CampaignSummaryProps {
  campaigns: Record<string, number>;
  title?: string;
  loading?: boolean;
  tooltipKey?: string;
}

export function CampaignSummaryChart({ campaigns, title = "Campanhas", loading, tooltipKey }: CampaignSummaryProps) {
  if (loading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-5 w-48 mb-4" />
        <Skeleton className="h-[120px] w-full" />
      </Card>
    );
  }

  const data = [
    { name: "Rascunho", value: campaigns.DRAFT || 0, color: CAMPAIGN_COLORS.DRAFT },
    { name: "Ativas", value: campaigns.ACTIVE || 0, color: CAMPAIGN_COLORS.ACTIVE },
    { name: "Encerradas", value: campaigns.CLOSED || 0, color: CAMPAIGN_COLORS.CLOSED },
  ];
  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <Card className="p-6">
      <div className="flex items-center gap-1 mb-4">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {tooltipKey && <IndicadorTooltip indicadorKey={tooltipKey} side="top" />}
      </div>
      {total === 0 ? (
        <div className="flex flex-col items-center justify-center h-[120px] text-muted-foreground gap-2">
          <Send size={32} className="opacity-30" />
          <div className="text-center">
            <p className="text-sm font-medium">Nenhuma campanha criada</p>
            <p className="text-xs mt-0.5">Crie uma campanha para iniciar a coleta de respostas.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4">
            {data.map((item, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl font-bold tabular-nums" style={{ color: item.color }}>
                  {item.value.toLocaleString("pt-BR")}
                </div>
                <div className="text-xs text-muted-foreground mt-1">{item.name}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex h-2 rounded-full overflow-hidden bg-muted">
            {data.map((item, i) => (
              item.value > 0 && (
                <div
                  key={i}
                  className="h-full"
                  style={{
                    width: `${(item.value / total) * 100}%`,
                    backgroundColor: item.color,
                  }}
                />
              )
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

// ─── Adesão Progress Bar ────────────────────────────────────
interface AdesaoBarProps {
  completed: number;
  total: number;
  label?: string;
}

export function AdesaoBar({ completed, total, label }: AdesaoBarProps) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const color = pct >= 80 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444";

  return (
    <div className="space-y-1">
      {label && <span className="text-xs text-muted-foreground">{label}</span>}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
        </div>
        <span className="text-xs font-medium tabular-nums w-10 text-right">{pct}%</span>
      </div>
      <span className="text-xs text-muted-foreground">{completed}/{total}</span>
    </div>
  );
}
